/**
 * Notice templates on the case page (UX02): the notice rows of the case's latest report, each with a
 * "Print template" action that opens the rendered template (the same renderer and the same rows the report page
 * uses). The report is read when the officer asks for the rows, not when the page opens, because building it
 * can take a minute on a large case.
 */
import { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { caseReportService } from '../../services/api';
import { useFeature } from '../../auth/features';
import type { CaseReportPayload } from '../report/types';
import { printNoticeTemplateFor, printableNoticeRows, type NoticeRowRef } from '../report/noticeTemplate';

export function NoticeTemplatesPanel({ caseId }: { caseId: string }) {
  const allowed = useFeature('legal.notices');
  const [report, setReport] = useState<CaseReportPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const current = useRef(caseId);
  current.current = caseId;

  // A different case clears what was loaded for the previous one (R8).
  useEffect(() => { setReport(null); setError(null); setBusy(null); }, [caseId]);

  if (!allowed) return null;

  const load = async () => {
    const asked = caseId;
    setLoading(true); setError(null);
    try {
      const data = await caseReportService.getReportData(asked);
      if (current.current === asked) setReport(data as CaseReportPayload);
    } catch (e) {
      if (current.current === asked) setError(e instanceof Error && e.message ? e.message : 'The report could not be loaded.');
    } finally {
      if (current.current === asked) setLoading(false);
    }
  };

  const print = async (ref: NoticeRowRef) => {
    if (!report) return;
    const key = `${ref.kind}:${ref.index}`;
    setBusy(key);
    try {
      await printNoticeTemplateFor(report, ref);
    } catch (e) {
      console.error('Failed to print the notice template', e);
      toast.error(e instanceof Error && e.message ? `Could not print the template: ${e.message}` : 'Could not print the template.');
    } finally {
      setBusy(null);
    }
  };

  const rows = report ? printableNoticeRows(report) : [];

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="notice-templates-panel">
      <h3 className="font-medium mb-1 text-sm flex items-center gap-1.5"><FileText className="w-4 h-4" /> Notice templates</h3>
      <p className="text-xs text-muted-foreground mb-3">
        The exchange notices of the latest case-worker run, each as a template to complete, print and serve.
      </p>
      {!report && (
        <button type="button" onClick={() => void load()} disabled={loading} data-testid="notice-templates-load"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md disabled:opacity-60">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null} {loading ? 'Reading the report…' : 'Show notice rows'}
        </button>
      )}
      {error && <p className="text-xs text-red-500 mt-2" role="alert">{error}</p>}
      {report && rows.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="notice-templates-empty">
          The latest run has no exchange notice rows. Run the case worker, then open this again.
        </p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-1.5">
          {rows.map(({ ref, label }) => (
            <li key={`${ref.kind}:${ref.index}`} className="flex items-center justify-between gap-2 text-xs">
              <span className="min-w-0 truncate" title={label}>{label}</span>
              <button type="button" onClick={() => void print(ref)} disabled={busy !== null}
                data-testid="notice-templates-print"
                className="shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50">
                {busy === `${ref.kind}:${ref.index}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3" />} Print template
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
